/**
 * useJournalAutosave — 日記「自由書寫」嘅**自動儲存**（debounce 900ms）
 *
 * 血緣：`decisions/027`（產品負責人：「there is some space for the user to **record their feeling**」）
 *   ＋「儲存狀態要**照實講**（儲存緊／已儲存／儲存唔到）」
 * 由 `pages/SongsPage.tsx` 拆出（`P-90`：頁面檔案硬規則 ≤200 行）
 *
 * ⚠️ **失敗要照實報**（唔准扮成功）—— 由父層嘅 `onError` 顯示。
 * ⚠️ 離開頁面 → 清 timer（唔會再寫）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSessionId } from './useChatStream';

export type JournalSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface JournalRow {
  id: string;
  journal?: string | null;
}

export function useJournalAutosave(
  rows: JournalRow[],
  opts: { deviceId: string | null; onError: (msg: string) => void },
) {
  const { t } = useTranslation();
  const { deviceId, onError } = opts;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<Record<string, JournalSaveState>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /** 草稿初始化（伺服器版本為準；已經有草稿就唔覆蓋） */
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const s of rows) if (next[s.id] === undefined) next[s.id] = s.journal ?? '';
      return next;
    });
  }, [rows]);

  /** 真儲存（`PATCH /api/song/:id/journal`）—— ⚠️ 失敗照實講 */
  const save = useCallback(
    async (songId: string, text: string) => {
      setSaveState((s) => ({ ...s, [songId]: 'saving' }));
      try {
        const res = await fetch(`/api/song/${songId}/journal`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: getSessionId(), deviceId, journal: text }),
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setSaveState((s) => ({ ...s, [songId]: res.ok ? 'saved' : 'error' }));
        if (!res.ok) onError(`${t('diary_save_failed')}${data.error ? `（${data.error}）` : ''}`);
      } catch (err) {
        setSaveState((s) => ({ ...s, [songId]: 'error' }));
        onError(`${t('diary_save_failed')}（${String(err).slice(0, 60)}）`);
      }
    },
    [deviceId, onError, t],
  );

  /** 打字 → 更新草稿 ＋ debounce 儲存 */
  const onChange = useCallback(
    (songId: string, text: string) => {
      setDrafts((d) => ({ ...d, [songId]: text }));
      setSaveState((s) => ({ ...s, [songId]: 'saving' }));
      if (timers.current[songId]) clearTimeout(timers.current[songId]);
      timers.current[songId] = setTimeout(() => void save(songId, text), 900);
    },
    [save],
  );

  /** 離開頁面 → 清 timer（唔會再寫） */
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of Object.values(pending)) clearTimeout(timer);
    };
  }, []);

  /** 狀態文字（`idle` → 空；其餘照實顯示） */
  const saveMark = useCallback(
    (state: JournalSaveState | undefined): string =>
      state === 'saving' || state === 'saved' || state === 'error'
        ? t(state === 'saving' ? 'diary_saving' : state === 'saved' ? 'diary_saved' : 'diary_save_failed')
        : '',
    [t],
  );

  return { drafts, saveState, save, onChange, saveMark };
}
